import { Box } from "@mui/material";
import { useEffect } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { Toolbar } from "../Toolbar";

interface AdvancedFeaturesToolbarProps {
    id?: string;
}

/**
 * Generic toolbar for consumer-injected advanced feature buttons (e.g. Omniverse).
 *
 * Always mounted so that consumer-registered buttons persist in the store.
 * Auto-hides via CSS (`display: none`) while a closable tool mode (Measure,
 * Section, etc.) is active — consumers do not need any visibility logic.
 *
 * @remarks
 * Currently rendered in the **Procore Web (desktop) HUD only**.
 * Mobile (`HudProcoreMobile`) does not mount this toolbar — calling
 * `addToolbarButton(CORE_TOOLBAR_IDS.ADVANCED_FEATURES, …)` in a mobile
 * session will warn and no-op. Mobile support is out of scope for now.
 */
export function AdvancedFeaturesToolbar({ id = "advanced-features-menu" }: AdvancedFeaturesToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isVisible = activeToolMode === ProcoreToolMode.Default;

    useEffect(() => {
        toolbarDispatch(toolbarsActions.addToolbar({ id, buttons: [] }));

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id]);

    return (
        <Box sx={{ display: isVisible ? undefined : "none" }}>
            <Toolbar id={id} />
        </Box>
    );
}
